/**
 * Utility functions for multi-resource mode in CondensedView.
 * Provides overlap grouping, color assignment, and adaptive font sizing.
 */

/**
 * Pastel color palette for distinguishing resources.
 * Each entry has a light background and medium-saturation border.
 * Used as fallback when no primaryColor is provided to buildColorMap.
 */
export const RESOURCE_PALETTE = [
  { bg: '#E3F2FD', border: '#90CAF9' }, // Blue
  { bg: '#FFF3E0', border: '#FFB74D' }, // Orange
  { bg: '#E8F5E9', border: '#81C784' }, // Green
  { bg: '#F3E5F5', border: '#CE93D8' }, // Purple
  { bg: '#FFF8E1', border: '#FFD54F' }, // Amber
  { bg: '#E0F7FA', border: '#4DD0E1' }, // Cyan
  { bg: '#FCE4EC', border: '#F48FB1' }, // Pink
  { bg: '#F1F8E9', border: '#AED581' }, // Light Green
  { bg: '#EDE7F6', border: '#B39DDB' }, // Deep Purple
  { bg: '#EFEBE9', border: '#BCAAA4' }, // Brown
];

// --- OKLCH conversion chain ---

/**
 * Parses a hex color string to [r, g, b] in 0-1 range.
 * Handles 3-digit (#RGB) and 6-digit (#RRGGBB) hex, with or without #.
 * Returns [0, 0, 0] on invalid input.
 */
export function hexToRgb(hex) {
  if (typeof hex !== 'string') return [0, 0, 0];
  let h = hex.replace(/^#/, '');
  if (h.length === 3) {
    h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  }
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return [0, 0, 0];
  return [
    parseInt(h.slice(0, 2), 16) / 255,
    parseInt(h.slice(2, 4), 16) / 255,
    parseInt(h.slice(4, 6), 16) / 255,
  ];
}

/** Inverse sRGB companding (gamma decode): sRGB → linear */
export function srgbToLinear(c) {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** sRGB companding (gamma encode): linear → sRGB */
export function linearToSrgb(c) {
  const clamped = Math.max(0, Math.min(1, c));
  return clamped <= 0.0031308
    ? clamped * 12.92
    : 1.055 * Math.pow(clamped, 1 / 2.4) - 0.055;
}

/** Linear sRGB → Oklab [L, a, b] */
export function linearRgbToOklab([r, g, b]) {
  const l_ = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m_ = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s_ = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);

  return [
    0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
    1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
    0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_,
  ];
}

/** Oklab [L, a, b] → linear sRGB [r, g, b] */
export function oklabToLinearRgb([L, a, b]) {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;

  const l3 = l_ * l_ * l_;
  const m3 = m_ * m_ * m_;
  const s3 = s_ * s_ * s_;

  return [
    +4.0767416621 * l3 - 3.3077115913 * m3 + 0.2309699292 * s3,
    -1.2684380046 * l3 + 2.6097574011 * m3 - 0.3413193965 * s3,
    -0.0041960863 * l3 - 0.7034186147 * m3 + 1.707614701 * s3,
  ];
}

/** Oklab [L, a, b] → OKLCH { l, c, h } (cartesian to polar) */
export function oklabToOklch([L, a, b]) {
  const c = Math.sqrt(a * a + b * b);
  let h = (Math.atan2(b, a) * 180) / Math.PI;
  if (h < 0) h += 360;
  return { l: L, c, h };
}

/** OKLCH (l, c, h) → Oklab [L, a, b] (polar to cartesian) */
export function oklchToOklab(l, c, h) {
  const hRad = (h * Math.PI) / 180;
  return [l, c * Math.cos(hRad), c * Math.sin(hRad)];
}

/**
 * Full chain: OKLCH → hex string.
 * Clamps to sRGB gamut (no perceptual gamut mapping).
 *
 * @param {number} l - Lightness (0-1)
 * @param {number} c - Chroma (0+)
 * @param {number} h - Hue in degrees (0-360)
 * @returns {string} Hex color string (e.g. '#f0a1b2')
 */
export function oklchToHex(l, c, h) {
  const lab = oklchToOklab(l, c, h);
  const [r, g, b] = oklabToLinearRgb(lab);
  const toByte = (v) => Math.max(0, Math.min(255, Math.round(linearToSrgb(v) * 255)));
  return (
    '#' +
    toByte(r).toString(16).padStart(2, '0') +
    toByte(g).toString(16).padStart(2, '0') +
    toByte(b).toString(16).padStart(2, '0')
  );
}

/** Full chain: hex → OKLCH { l, c, h } */
export function hexToOklch(hex) {
  const [r, g, b] = hexToRgb(hex);
  const linRgb = [srgbToLinear(r), srgbToLinear(g), srgbToLinear(b)];
  const lab = linearRgbToOklab(linRgb);
  return oklabToOklch(lab);
}

// --- Resource color generation ---

/**
 * Generates N resource colors dynamically from the theme's primary color.
 * Uses OKLCH color space for perceptually uniform hue distribution.
 * A 60° exclusion zone around the primary hue ensures resource colors
 * are visually distinct from the primary (used for button text/borders).
 *
 * @param {number} n - Number of resource colors to generate
 * @param {string} primaryColor - Primary theme color as hex string
 * @param {string} [mode='light'] - 'light' or 'dark' theme mode
 * @returns {Array<{bg: string, border: string}>} Array of color objects
 */
export function generateResourceColors(n, primaryColor, mode = 'light') {
  const { h: primaryHue } = hexToOklch(primaryColor);
  const exclusionAngle = 30; // ±30° = 60° total exclusion zone
  const availableArc = 360 - exclusionAngle * 2; // 300°

  const isDark = mode === 'dark';
  const bgL = isDark ? 0.25 : 0.95;
  const bgC = 0.03;
  const borderL = isDark ? 0.7 : 0.78;
  const borderC = 0.11;

  const colors = [];
  for (let i = 0; i < n; i++) {
    const hue = (primaryHue + exclusionAngle + i * (availableArc / n)) % 360;
    colors.push({
      bg: oklchToHex(bgL, bgC, hue),
      border: oklchToHex(borderL, borderC, hue),
    });
  }
  return colors;
}

// --- Existing utilities ---

/**
 * Groups overlapping timeslots into arrays of concurrent timeslots.
 * Uses a sweep-line algorithm (O(n)) on pre-sorted timeslots.
 *
 * @param {Array} timeslots - Day's timeslots sorted by start time (from useBookableCurator)
 * @returns {Array<Array>} Groups of overlapping timeslots
 */
export function groupOverlapping(timeslots) {
  if (!timeslots || timeslots.length === 0) return [];

  const groups = [];
  let currentGroup = [timeslots[0]];
  let latestEnd = timeslots[0].end;

  for (let i = 1; i < timeslots.length; i++) {
    const tsl = timeslots[i];

    if (tsl.start.toMillis() < latestEnd.toMillis()) {
      // Overlaps with current group
      currentGroup.push(tsl);
      if (tsl.end.toMillis() > latestEnd.toMillis()) {
        latestEnd = tsl.end;
      }
    } else {
      // No overlap - finalize current group, start new one
      groups.push(currentGroup);
      currentGroup = [tsl];
      latestEnd = tsl.end;
    }
  }

  // Don't forget the last group
  groups.push(currentGroup);

  return groups;
}

/**
 * Sub-groups an overlap group by timeslot UUID.
 * Bookables sharing the same UUID stack vertically (same column),
 * while different UUIDs remain side-by-side (separate columns).
 *
 * @param {Array} group - Flat array of timeslots from one overlap group
 * @returns {Array<Array>} Array of columns, each containing timeslots with the same UUID
 */
export function subGroupByUuid(group) {
  if (!group || group.length <= 1) return group ? [group] : [];

  const uuidMap = new Map();
  for (const tsl of group) {
    const key = tsl.uuid || tsl.timeslot_uuid;
    if (!key) {
      uuidMap.set(Symbol(), [tsl]);
      continue;
    }
    if (!uuidMap.has(key)) uuidMap.set(key, []);
    uuidMap.get(key).push(tsl);
  }

  return Array.from(uuidMap.entries())
    .sort((a, b) => {
      const aKey = typeof a[0] === 'string' ? a[0] : '';
      const bKey = typeof b[0] === 'string' ? b[0] : '';
      return aKey.localeCompare(bKey);
    })
    .map(([, timeslots]) => timeslots);
}

/**
 * Builds a stable color map from resource UUIDs to palette colors.
 * Sorts UUIDs lexicographically for consistent assignment across re-renders.
 *
 * When primaryColor is provided, generates colors dynamically using OKLCH
 * with a 60° exclusion zone around the primary hue. Falls back to the
 * static RESOURCE_PALETTE when primaryColor is falsy.
 *
 * @param {Object} publicDataMap - Map of resource UUID -> publicData
 * @param {string} [primaryColor] - Theme primary color as hex (e.g. '#1976d2')
 * @param {string} [mode='light'] - 'light' or 'dark' theme mode
 * @returns {Object} Map of resource UUID -> { bg, border }
 */
export function buildColorMap(publicDataMap, primaryColor, mode) {
  if (!publicDataMap) return {};

  const uuids = Object.keys(publicDataMap)
    .filter((uuid) => uuid !== 'all-option')
    .sort();

  if (primaryColor) {
    const palette = generateResourceColors(uuids.length, primaryColor, mode);
    const colorMap = {};
    uuids.forEach((uuid, index) => {
      colorMap[uuid] = palette[index];
    });
    return colorMap;
  }

  const colorMap = {};
  uuids.forEach((uuid, index) => {
    colorMap[uuid] = RESOURCE_PALETTE[index % RESOURCE_PALETTE.length];
  });

  return colorMap;
}

/**
 * Returns adaptive font size based on number of parallel timeslots in a group.
 *
 * @param {number} parallelCount - Number of side-by-side timeslots
 * @returns {string} CSS font-size value
 */
export function adaptiveFontSize(parallelCount) {
  if (parallelCount <= 2) return '0.75rem';
  if (parallelCount <= 3) return '0.625rem';
  if (parallelCount <= 5) return '0.5625rem';
  return '0.5rem';
}
